import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '../assets/svgImages';
import { Product, SearchBarProps } from '../interface/interface';
import { useProductSearch } from '../hooks/useProductSearch';

const SearchBar = ({
  products = [],
  onSearch,
  isGuest = false,
}: SearchBarProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const {
    value,
    setValue,
    suggestions,
    showDropdown,
    setShowDropdown,
    handleChange,
  } = useProductSearch(products, onSearch);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSuggestionClick = (product: Product) => {
    setShowDropdown(false);
    setValue('');
    if (isGuest) {
      navigate(`/guest/product/${product.product_code}`, {
        state: {
          from: '/guest/shop',
        },
      });
      return;
    }
    navigate(`/product/patient/${product.product_code}`, {
      state: {
        from: '/home',
      },
    });
  };

  return (
    <div className="relative w-84 xl:w-100" ref={wrapperRef}>
      <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-blue-400 z-10" />
      <input
        name="globalSearch"
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={'Search for products, brands...'}
        className="w-full pl-12 pr-11 py-2.5 rounded-full bg-white text-primary-blue-600
          placeholder:text-primary-blue-400 text-xs
          focus:outline-none focus:ring-2 focus:ring-white/70 focus:bg-white
          transition-colors shadow-sm truncate h-12"
      />

      {showDropdown && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-2xl shadow-lg border border-gray-100 z-[99] overflow-hidden">
          <div
            className="max-h-96 overflow-y-auto overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
          >
            {suggestions?.length ? (
              suggestions.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleSuggestionClick(product)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <img
                    src={product.image_url || '/default-image.png'}
                    alt={product.name}
                    className="w-9 h-9 object-contain rounded-md border border-gray-100 bg-white flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700 truncate">
                    {product.name}
                  </span>
                </button>
              ))
            ) : (
              <p className="px-4 py-3 text-sm text-gray-500">
                No Products found to display
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
